import { formatPhoneDisplay } from '@/app/(main)/clients/utils/formatters';
import type { EmailWriteAction, PhoneWriteAction } from '@/types/clientDetailsApiTypes';

export type ContactEmail = {
  uuid?: string | null;
  title?: string | null;
  email?: string | null;
  is_primary?: boolean;
};

export type ContactPhone = {
  uuid?: string | null;
  title?: string | null;
  phone?: string | null;
  raw_phone?: string | null;
  is_primary?: boolean;
};

type ContactSource = {
  email?: string | null;
  additional_emails?: { email?: string | null }[] | null;
  emails?: ContactEmail[] | null;
  phone?: string | null;
  phones?: ContactPhone[] | null;
};

type DesiredEmail = {
  email: string;
  is_primary: boolean;
};

type DesiredPhone = {
  raw: string;
  digits: string;
  title?: string | null;
  is_primary: boolean;
};

export const CONTACT_TITLES = {
  primary: 'Primary',
  secondary: 'Secondary',
} as const;

const digitsOf = (value: string | null | undefined): string => (value ?? '').replace(/\D/g, '');

const normalizeEmail = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

/** The BFF may keep the Primary title after a write even when `is_primary` is missing or false. */
function isPrimaryContact(row: { is_primary?: boolean; title?: string | null }): boolean {
  return row.is_primary === true || row.title?.trim() === CONTACT_TITLES.primary;
}

function contactTitle(isPrimary: boolean, title?: string | null): string {
  const trimmed = title?.trim() ?? '';

  return trimmed || (isPrimary ? CONTACT_TITLES.primary : CONTACT_TITLES.secondary);
}

export function additionalPhones<T extends { phone?: string | null; is_primary?: boolean; title?: string | null }>(
  phones: T[] | null | undefined
): T[] {
  return (phones ?? []).filter((phone) => !isPrimaryContact(phone));
}

export function displayedEmail(source: ContactSource | null | undefined): string {
  const rows = (source?.emails ?? []).filter((row) => normalizeEmail(row.email) !== '');
  const primary = rows.find(isPrimaryContact)?.email ?? rows[0]?.email;

  return (primary || source?.email || '').trim();
}

export function displayedPhone(source: ContactSource | null | undefined): string {
  const rows = source?.phones ?? [];
  const primary = rows.find(isPrimaryContact);
  const fromList = (primary?.raw_phone || primary?.phone || '').trim();
  if (fromList) return fromList;
  if (rows.some(isPrimaryContact)) return '';

  return (source?.phone || '').trim();
}

export function formContacts(source: ContactSource | null | undefined): {
  email: string;
  additional_emails: { email: string }[];
  phone: string;
  phones: { title: string; phone: string }[];
} {
  const emailRows = (source?.emails ?? []).filter((row) => normalizeEmail(row.email) !== '');
  const phoneRows = source?.phones ?? [];
  const phonesAreComplete = phoneRows.some(isPrimaryContact);
  const primaryEmail = displayedEmail(source).toLowerCase();

  return {
    email: displayedEmail(source),
    additional_emails: emailRows.length
      ? emailRows
          .filter((row) => normalizeEmail(row.email) !== primaryEmail)
          .map((row) => ({ email: (row.email ?? '').trim() }))
      : (source?.additional_emails ?? [])
          .filter((row) => normalizeEmail(row.email) !== '')
          .map((row) => ({ email: (row.email ?? '').trim() })),
    phone: displayedPhone(source),
    phones: (phonesAreComplete ? phoneRows.filter((row) => !isPrimaryContact(row)) : additionalPhones(phoneRows)).map(
      (row) => ({
        title: contactTitle(false, row.title),
        phone: (row.raw_phone || row.phone || '').trim(),
      })
    ),
  };
}

type EmailLine = {
  email?: string | null;
  title?: string | null;
  is_primary?: boolean;
};

export function contactEmailLines(source: ContactSource | null | undefined): { labels: string[]; copyValues: string[] } {
  const rows = (source?.emails ?? []).filter((row) => normalizeEmail(row.email) !== '');
  const emails: EmailLine[] = rows.length
    ? rows
    : [
        ...(source?.email ? [{ email: source.email, is_primary: true }] : []),
        ...(source?.additional_emails ?? [])
          .filter((row) => normalizeEmail(row.email) !== '')
          .map((row) => ({ email: row.email })),
      ];

  const labels = emails.map((row) => {
    const address = (row.email ?? '').trim();

    return `${address} (${contactTitle(isPrimaryContact(row), row.title)})`;
  });

  return {
    labels,
    copyValues: emails.map((row) => (row.email ?? '').trim()),
  };
}

export function contactPhoneLines(source: ContactSource | null | undefined): { labels: string[]; copyValues: string[] } {
  const rows = source?.phones ?? [];
  const listIsComplete = rows.some(isPrimaryContact);
  const phones = listIsComplete
    ? rows
    : [
        ...(source?.phone ? [{ phone: source.phone, raw_phone: source.phone, is_primary: true, title: null }] : []),
        ...rows.filter((row) => row.is_primary !== true),
      ];

  const visible = phones.filter((row) => digitsOf(row.raw_phone || row.phone) !== '');
  const labels = visible.map((row) => {
    const shown = displayPhone(row);

    return `${shown} (${contactTitle(isPrimaryContact(row), row.title)})`;
  });

  return {
    labels,
    copyValues: visible.map((row) => displayPhone(row)),
  };
}

type ContactForm = {
  email?: string | null;
  additional_emails?: { email?: string | null }[] | null;
  phone?: string | null;
  phones?: { title?: string | null; phone?: string | null }[] | null;
};

/**
 * Full client-details saves send emails[] and phones[] actions when the loaded
 * client already has those lists. A save that never received them keeps the
 * legacy email / additional_emails / phone / phones fields.
 */
export function contactWrite(
  form: ContactForm,
  storedEmails: ContactEmail[] | null | undefined,
  storedPhones: ContactPhone[] | null | undefined
):
  | {
      emails?: EmailWriteAction[];
      phones?: PhoneWriteAction[];
    }
  | {
      email: string | null;
      additional_emails: { email: string }[];
      phone: string | null;
      phones: { title: string | null; phone: string }[];
    } {
  if (!Array.isArray(storedEmails) || !Array.isArray(storedPhones)) {
    return {
      email: form.email ?? null,
      additional_emails: (form.additional_emails ?? [])
        .filter((row): row is { email: string } => typeof row.email === 'string')
        .map((row) => ({ email: row.email })),
      phone: form.phone ?? null,
      phones: (form.phones ?? []).map((row) => ({
        title: row.title ?? null,
        phone: row.phone ?? '',
      })),
    };
  }

  const emails = buildEmailActions(form, storedEmails);
  const phones = buildPhoneActions(form, storedPhones);

  return {
    ...(emails.length ? { emails } : {}),
    ...(phones.length ? { phones } : {}),
  };
}

export function buildEmailActions(
  form: { email?: string | null; additional_emails?: { email?: string | null }[] | null },
  stored: ContactEmail[] | null | undefined
): EmailWriteAction[] {
  const desired: DesiredEmail[] = [];
  const primary = normalizeEmail(form.email);
  if (primary) desired.push({ email: primary, is_primary: true });
  for (const extra of form.additional_emails ?? []) {
    const email = normalizeEmail(extra.email);
    if (email && !desired.some((row) => row.email === email)) {
      desired.push({ email, is_primary: false });
    }
  }

  return diffEmails(desired, stored ?? []);
}

const CREATE_CONTACT_FIELD =
  /^(primary_passenger|related_passengers\.\d+)\.client\.(email|additional_emails|phone|phones)(?:\.|$)/;

export type CreateContactField = {
  prefix: string;
  kind: 'email' | 'additional_emails' | 'phone' | 'phones';
};

/** Create-form autosave fields. Draft blur uses another payload and does not match. */
export function createContactField(fieldName: string): CreateContactField | null {
  const match = CREATE_CONTACT_FIELD.exec(fieldName);
  if (!match) return null;

  return {
    prefix: match[1],
    kind: match[2] as CreateContactField['kind'],
  };
}

/**
 * One create-form field at a time. Only the touched role is inserted, updated,
 * or deleted, so a primary blur does not remove additional rows.
 * Returns null when the stored rows already match and no request is needed.
 */
export function contactFieldActions(
  kind: CreateContactField['kind'],
  value: unknown,
  storedEmails: ContactEmail[] | null | undefined = [],
  storedPhones: ContactPhone[] | null | undefined = []
): { emails: EmailWriteAction[] } | { phones: PhoneWriteAction[] } | null {
  if (kind === 'email') {
    const emails = primaryEmailActions(value, storedEmails ?? []);
    return emails.length ? { emails } : null;
  }
  if (kind === 'additional_emails') {
    const emails = additionalEmailActions(asEmailRows(value), storedEmails ?? []);
    return emails.length ? { emails } : null;
  }
  if (kind === 'phone') {
    const phones = primaryPhoneActions(value, storedPhones ?? []);
    return phones.length ? { phones } : null;
  }

  const phones = additionalPhoneActions(asPhoneRows(value), storedPhones ?? []);
  return phones.length ? { phones } : null;
}

/** Quick add sends only the primary email and phone, after the empty client exists. */
export function primaryContactActions(
  email: string | null | undefined,
  phone: string | null | undefined,
  storedEmails: ContactEmail[] | null | undefined,
  storedPhones: ContactPhone[] | null | undefined
): { emails?: EmailWriteAction[]; phones?: PhoneWriteAction[] } {
  const emails = primaryEmailActions(email, storedEmails ?? []);
  const phones = primaryPhoneActions(phone, storedPhones ?? []);

  return {
    ...(emails.length ? { emails } : {}),
    ...(phones.length ? { phones } : {}),
  };
}

export function contactsFromDetailsResponse(response: unknown): { emails: ContactEmail[]; phones: ContactPhone[] } | null {
  if (!response || typeof response !== 'object') return null;
  const data = (response as { data?: { client?: { emails?: ContactEmail[]; phones?: ContactPhone[] } } }).data?.client;
  if (!data) return null;
  if (!Array.isArray(data.emails) && !Array.isArray(data.phones)) return null;

  return {
    emails: Array.isArray(data.emails) ? data.emails : [],
    phones: Array.isArray(data.phones) ? data.phones : [],
  };
}

function primaryEmailActions(value: unknown, stored: ContactEmail[]): EmailWriteAction[] {
  const email = normalizeEmail(typeof value === 'string' ? value : '');
  const primary = stored.find((row) => row.uuid && row.is_primary === true);

  if (!email) {
    return primary?.uuid ? [{ action: 'DELETE', uuid: primary.uuid }] : [];
  }
  if (primary?.uuid) {
    if (normalizeEmail(primary.email) === email) return [];
    return [{ action: 'UPDATE', uuid: primary.uuid, email, is_primary: true }];
  }

  return [{ action: 'INSERT', email, is_primary: true, title: CONTACT_TITLES.primary }];
}

function additionalEmailActions(
  rows: { email?: string | null }[],
  stored: ContactEmail[]
): EmailWriteAction[] {
  const extras = stored.filter((row) => row.uuid && row.is_primary !== true);
  const desired: string[] = [];
  for (const row of rows) {
    const email = normalizeEmail(row.email);
    if (email && !desired.includes(email)) desired.push(email);
  }

  const used = new Set<string>();
  const actions: EmailWriteAction[] = [];
  for (const email of desired) {
    const match = extras.find((item) => item.uuid && !used.has(item.uuid) && normalizeEmail(item.email) === email);
    if (match?.uuid) {
      used.add(match.uuid);
      continue;
    }
    actions.push({ action: 'INSERT', email, is_primary: false, title: CONTACT_TITLES.secondary });
  }
  for (const item of extras) {
    if (item.uuid && !used.has(item.uuid)) actions.push({ action: 'DELETE', uuid: item.uuid });
  }

  return actions;
}

function primaryPhoneActions(value: unknown, stored: ContactPhone[]): PhoneWriteAction[] {
  const raw = typeof value === 'string' ? value.trim() : '';
  const digits = digitsOf(raw);
  const primary = stored.find((row) => row.uuid && row.is_primary === true);

  if (!digits) {
    return primary?.uuid ? [{ action: 'DELETE', uuid: primary.uuid }] : [];
  }
  if (primary?.uuid) {
    const storedDigits = digitsOf(primary.raw_phone || primary.phone);
    if (storedDigits === digits) {
      const storedRaw = (primary.raw_phone || '').trim();
      if (storedRaw && storedRaw !== raw) {
        return [{ action: 'UPDATE', uuid: primary.uuid, raw_phone: raw, is_primary: true }];
      }
      return [];
    }

    return [
      { action: 'DELETE', uuid: primary.uuid },
      { action: 'INSERT', raw_phone: raw, is_primary: true, title: CONTACT_TITLES.primary },
    ];
  }

  return [{ action: 'INSERT', raw_phone: raw, is_primary: true, title: CONTACT_TITLES.primary }];
}

function additionalPhoneActions(rows: { title?: string | null; phone?: string | null }[], stored: ContactPhone[]): PhoneWriteAction[] {
  const extras = stored.filter((row) => row.uuid && row.is_primary !== true);
  const desired: DesiredPhone[] = [];
  for (const extra of rows) {
    const digits = digitsOf(extra.phone);
    if (!digits || desired.some((row) => row.digits === digits)) continue;
    desired.push({
      raw: (extra.phone ?? '').trim(),
      digits,
      title: contactTitle(false, extra.title),
      is_primary: false,
    });
  }

  const used = new Set<string>();
  const actions: PhoneWriteAction[] = [];
  for (const row of desired) {
    const match = extras.find(
      (item) => item.uuid && !used.has(item.uuid) && digitsOf(item.raw_phone || item.phone) === row.digits
    );
    if (match?.uuid) {
      used.add(match.uuid);
      const titleChanged = (row.title ?? '') !== (match.title ?? '');
      const rawChanged = row.raw !== (match.raw_phone || '').trim();
      if (titleChanged || rawChanged) {
        const update: PhoneWriteAction = { action: 'UPDATE', uuid: match.uuid, is_primary: false };
        if (titleChanged) update.title = contactTitle(false, row.title);
        if (rawChanged) update.raw_phone = row.raw;
        actions.push(update);
      }
      continue;
    }
    actions.push({
      action: 'INSERT',
      raw_phone: row.raw,
      is_primary: false,
      title: contactTitle(false, row.title),
    });
  }
  for (const item of extras) {
    if (item.uuid && !used.has(item.uuid)) actions.push({ action: 'DELETE', uuid: item.uuid });
  }

  return actions;
}

function asEmailRows(value: unknown): { email?: string | null }[] {
  return Array.isArray(value) ? value : [];
}

function asPhoneRows(value: unknown): { title?: string | null; phone?: string | null }[] {
  return Array.isArray(value) ? value : [];
}

export function buildPhoneActions(
  form: { phone?: string | null; phones?: { title?: string | null; phone?: string | null }[] | null },
  stored: ContactPhone[] | null | undefined
): PhoneWriteAction[] {
  const desired: DesiredPhone[] = [];
  const primaryDigits = digitsOf(form.phone);
  if (primaryDigits) {
    desired.push({ raw: (form.phone ?? '').trim(), digits: primaryDigits, is_primary: true });
  }
  for (const extra of form.phones ?? []) {
    const digits = digitsOf(extra.phone);
    if (!digits || desired.some((row) => row.digits === digits)) continue;
    desired.push({
      raw: (extra.phone ?? '').trim(),
      digits,
      title: contactTitle(false, extra.title),
      is_primary: false,
    });
  }

  return diffPhones(desired, stored ?? []);
}

function diffEmails(desired: DesiredEmail[], stored: ContactEmail[]): EmailWriteAction[] {
  const used = new Set<string>();
  const actions: EmailWriteAction[] = [];

  for (const row of desired) {
    const match = stored.find((item) => item.uuid && normalizeEmail(item.email) === row.email && !used.has(item.uuid));
    if (match?.uuid) {
      used.add(match.uuid);
      if (Boolean(match.is_primary) !== row.is_primary) {
        const nextTitle = contactTitle(row.is_primary, match.title);
        actions.push({
          action: 'UPDATE',
          uuid: match.uuid,
          is_primary: row.is_primary,
          ...(nextTitle !== (match.title?.trim() ?? '') ? { title: nextTitle } : {}),
        });
      }
      continue;
    }
    actions.push({
      action: 'INSERT',
      email: row.email,
      is_primary: row.is_primary,
      title: contactTitle(row.is_primary),
    });
  }

  for (const item of stored) {
    if (item.uuid && !used.has(item.uuid)) {
      actions.push({ action: 'DELETE', uuid: item.uuid });
    }
  }

  return actions;
}

function diffPhones(desired: DesiredPhone[], stored: ContactPhone[]): PhoneWriteAction[] {
  const used = new Set<string>();
  const actions: PhoneWriteAction[] = [];

  for (const row of desired) {
    const match = stored.find((item) => {
      if (!item.uuid || used.has(item.uuid)) return false;
      return digitsOf(item.raw_phone || item.phone) === row.digits;
    });
    if (match?.uuid) {
      used.add(match.uuid);
      const roleChanged = Boolean(match.is_primary) !== row.is_primary;
      const nextTitle = contactTitle(row.is_primary, row.title ?? match.title);
      const titleChanged =
        (row.title !== undefined && nextTitle !== (match.title?.trim() ?? '')) ||
        (roleChanged && nextTitle !== (match.title?.trim() ?? ''));

      if (roleChanged || titleChanged) {
        const update: PhoneWriteAction = { action: 'UPDATE', uuid: match.uuid, is_primary: row.is_primary };
        if (titleChanged) update.title = nextTitle;
        actions.push(update);
      }
      continue;
    }
    actions.push({
      action: 'INSERT',
      raw_phone: row.raw,
      is_primary: row.is_primary,
      title: contactTitle(row.is_primary, row.title),
    });
  }

  for (const item of stored) {
    if (item.uuid && !used.has(item.uuid)) {
      actions.push({ action: 'DELETE', uuid: item.uuid });
    }
  }

  return actions;
}

function displayPhone(phone: ContactPhone): string {
  const raw = phone.raw_phone?.trim() ?? '';
  const source = raw || phone.phone || '';
  if (!source) return '';
  const beyondPlus = raw.replace(/^\+/, '');
  if (raw && /\D/.test(beyondPlus)) return raw;
  return formatPhoneDisplay(source);
}
